// src/components/Seller/SellerUpdateProduct.js
import React, { useState } from "react";
import "../ProductManagement/Products.css";
import config from "../../config";

export default function SellerUpdateProduct({
  sellerId,
  product,
  onClose,
  onProductUpdated,
}) {
  const [currentStep, setCurrentStep] = useState(1);

  // 👇 Make it safe even if `product` is undefined
  const safeProduct = product || {};

  const [form, setForm] = useState(() => ({
    productName: safeProduct.productName || "",
    color: safeProduct.color || "",
    productDescription: safeProduct.productDescription || "",
    categoryName: safeProduct.category?.name || safeProduct.categoryName || "",
    subCategoryName:
      safeProduct.subCategory?.subCategoryName ||
      safeProduct.subCategoryName ||
      "",
    active: safeProduct.active ?? false,
    details: {
      ...(safeProduct.details || {}),
    },
    variants:
      safeProduct.variants?.map(v => ({
        variantId: v.variantId || v.id,
        sku: v.sku,
        sizeName: v.size?.name || v.sizeName,
        originalPrice: v.originalPrice,
        offerPrice:
          v.offerPrice ||
          (v.originalPrice &&
            (v.originalPrice * (1 - (v.discount || 0) / 100))),
        discount: v.discount || 0,
        stockQuantity: v.stockQuantity || 0,
      })) || [],
    images:
      safeProduct.images?.map((img, idx) => ({
        image_url: img.image_url,
        image_type: img.image_type || "GALLERY",
        display_order: img.display_order || idx + 1,
      })) || [],
  }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const categories = ["Boys", "Girls", "Ladies", "Men", "Women"];
  const subCategories = {
    Boys: ["Shirts", "T-Shirts", "Pants", "Shorts", "Jackets", "Shoes"],
    Girls: ["Dresses", "Tops", "Skirts", "Pants", "Jackets", "Shoes"],
    Ladies: ["Kurtis", "Sarees", "Tops", "Dresses", "Pants", "Jackets"],
    Men: ["Shirts", "T-Shirts", "Pants", "Jeans", "Jackets"],
    Women: ["Sarees", "Kurtis", "Dresses", "Tops", "Bottoms"],
  };
  const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
  const colors = [
    "Red",
    "Blue",
    "Green",
    "Black",
    "White",
    "Yellow",
    "Orange",
    "Pink",
    "Purple",
    "Brown",
    "Gray",
    "Navy",
    "Maroon",
    "Dark Blue",
    "Light Blue",
    "Beige",
    "Cream",
    "Gold",
    "Silver",
    "Olive",
  ];

  const handleChange = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleDetailsChange = (field, value) =>
    setForm(prev => ({
      ...prev,
      details: { ...prev.details, [field]: value },
    }));

  const handleVariantChange = (idx, field, value) => {
    const variants = [...form.variants];
    variants[idx] = { ...variants[idx], [field]: value };

    if (field === "discount" || field === "originalPrice") {
      const originalPrice = parseFloat(variants[idx].originalPrice) || 0;
      const discount = parseFloat(variants[idx].discount) || 0;
      variants[idx].offerPrice = (
        originalPrice * (1 - discount / 100)
      ).toFixed(2);
    }

    setForm(prev => ({ ...prev, variants }));
  };

  const removeVariant = idx => {
    if (form.variants.length <= 1) return;
    setForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== idx),
    }));
  };

  const addVariant = () => {
    setForm(prev => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          sku: "",
          sizeName: "",
          originalPrice: "",
          discount: "",
          offerPrice: "",
          stockQuantity: "",
        },
      ],
    }));
  };

  const handleImageChange = (index, field, value) => {
    const images = [...form.images];
    images[index] = { ...images[index], [field]: value };
    setForm(prev => ({ ...prev, images }));
  };

  const addImageRow = () => {
    setForm(prev => ({
      ...prev,
      images: [
        ...prev.images,
        {
          image_url: "",
          image_type: "GALLERY",
          display_order: prev.images.length + 1,
        },
      ],
    }));
  };

  const removeImageRow = index => {
    if (form.images.length <= 1) return;
    const images = form.images
      .filter((_, i) => i !== index)
      .map((img, i) => ({
        ...img,
        display_order: i + 1,
      }));
    setForm(prev => ({ ...prev, images }));
  };

  const goToNextStep = () =>
    setCurrentStep(prev => Math.min(prev + 1, 4));
  const goToPrevStep = () =>
    setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!sellerId) throw new Error("Missing seller id");

      // 👇 use safeProduct so it doesn't crash when product is undefined
      const productId = safeProduct.productId || safeProduct.id;
      if (!productId) {
        throw new Error(
          "Missing product id. This screen is for editing an existing product. " +
            'For creating a new product, please use "Add Product" → SellerCreateProductForm.'
        );
      }

      if (
        !form.productName ||
        !form.color ||
        !form.categoryName ||
        !form.subCategoryName
      ) {
        throw new Error("Please fill all required fields");
      }

      const validVariants = form.variants.filter(
        v => v.sizeName && v.sku && v.originalPrice
      );
      if (validVariants.length === 0) {
        throw new Error("Please keep at least one valid variant");
      }

      const imagesPayload = form.images
        .filter(img => img.image_url)
        .map((img, idx) => ({
          image_url: img.image_url,
          image_type: img.image_type || (idx === 0 ? "MAIN" : "GALLERY"),
          display_order: idx + 1,
        }));

      const payload = {
        productName: form.productName,
        productDescription: form.productDescription,
        color: form.color,
        details: { ...form.details },
        categoryName: form.categoryName,
        subCategoryName: form.subCategoryName,
        variants: validVariants.map(v => ({
          sku: v.sku,
          sizeName: v.sizeName,
          originalPrice: parseFloat(v.originalPrice),
          offerPrice: parseFloat(v.offerPrice || 0),
          discount: parseFloat(v.discount || 0),
          stockQuantity: parseInt(v.stockQuantity || 0, 10),
        })),
        images: imagesPayload,
      };

      const res = await fetch(
        `${config.baseUrl}/api/seller/products/${sellerId}/${productId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to update seller product");
      }

      setSuccess("Product updated successfully!");
      setTimeout(() => {
        onProductUpdated && onProductUpdated();
      }, 900);
    } catch (err) {
      console.error("Seller update product error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <form
        className="modal-content large-modal"
        onSubmit={handleSubmit}
      >
        <div className="modal-header">
          <h2>Update Product</h2>
          <div className="step-indicator">
            {[1, 2, 3, 4].map(step => (
              <span
                key={step}
                className={
                  currentStep === step
                    ? "active"
                    : currentStep > step
                    ? "completed"
                    : ""
                }
              >
                {step}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {success && (
            <div className="success-message">{success}</div>
          )}

          {/* Step 1 */}
          {currentStep === 1 && (
            <div className="form-section">
              <h3>Step 1: Basic Product Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Product Name *</label>
                  <input
                    value={form.productName}
                    onChange={e =>
                      handleChange("productName", e.target.value)
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Color *</label>
                  <select
                    value={form.color}
                    onChange={e =>
                      handleChange("color", e.target.value)
                    }
                    required
                  >
                    <option value="">Select Color</option>
                    {colors.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={form.categoryName}
                    onChange={e =>
                      handleChange("categoryName", e.target.value)
                    }
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sub Category *</label>
                  <select
                    value={form.subCategoryName}
                    onChange={e =>
                      handleChange("subCategoryName", e.target.value)
                    }
                    required
                    disabled={!form.categoryName}
                  >
                    <option value="">Select Sub Category</option>
                    {form.categoryName &&
                      subCategories[form.categoryName]?.map(sub => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Product Description</label>
                <textarea
                  rows={3}
                  value={form.productDescription}
                  onChange={e =>
                    handleChange(
                      "productDescription",
                      e.target.value
                    )
                  }
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {currentStep === 2 && (
            <div className="form-section">
              <h3>Step 2: Additional Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fabric</label>
                  <input
                    value={form.details.fabric || ""}
                    onChange={e =>
                      handleDetailsChange("fabric", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Pattern</label>
                  <input
                    value={form.details.pattern || ""}
                    onChange={e =>
                      handleDetailsChange("pattern", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Hemline</label>
                  <input
                    value={form.details.hemline || ""}
                    onChange={e =>
                      handleDetailsChange("hemline", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Occasion</label>
                  <input
                    value={form.details.occasion || ""}
                    onChange={e =>
                      handleDetailsChange("occasion", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    value={form.details.brand || ""}
                    onChange={e =>
                      handleDetailsChange("brand", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Generic Name</label>
                  <input
                    value={form.details.genericName || ""}
                    onChange={e =>
                      handleDetailsChange("genericName", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Variants & Pricing
                </button>
              </div>
            </div>
          )}

          {/* Step 3 – variants */}
          {currentStep === 3 && (
            <div className="form-section">
              <h3>Step 3: Variants & Pricing</h3>
              <div className="variants-container">
                {form.variants.map((variant, idx) => (
                  <div key={idx} className="variant-card">
                    <div className="variant-header">
                      <h5>Variant {idx + 1}</h5>
                      {form.variants.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeVariant(idx)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="variant-grid">
                      <div className="form-group">
                        <label>Size *</label>
                        <select
                          value={variant.sizeName}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "sizeName",
                              e.target.value
                            )
                          }
                        >
                          <option value="">Select Size</option>
                          {sizes.map(s => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>SKU *</label>
                        <input
                          value={variant.sku}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "sku",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Original Price *</label>
                        <input
                          type="number"
                          value={variant.originalPrice}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "originalPrice",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Discount %</label>
                        <input
                          type="number"
                          value={variant.discount}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "discount",
                              e.target.value
                            )
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Offer Price</label>
                        <input
                          type="number"
                          value={variant.offerPrice}
                          readOnly
                          className="readonly-field"
                        />
                      </div>
                      <div className="form-group">
                        <label>Stock Quantity *</label>
                        <input
                          type="number"
                          value={variant.stockQuantity}
                          onChange={e =>
                            handleVariantChange(
                              idx,
                              "stockQuantity",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn-add-variant"
                  onClick={addVariant}
                >
                  + Add Another Variant
                </button>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Images
                </button>
              </div>
            </div>
          )}

          {/* Step 4 – images */}
          {currentStep === 4 && (
            <div className="form-section">
              <h3>Step 4: Image URLs</h3>
              <p className="step-description">
                Update / add image URLs. First image will be treated as
                MAIN.
              </p>

              {form.images.map((img, idx) => (
                <div key={idx} className="form-grid">
                  <div className="form-group full-width">
                    <label>
                      Image URL {idx + 1}{" "}
                      {idx === 0 && <span>(Main)</span>}
                    </label>
                    <input
                      value={img.image_url}
                      onChange={e =>
                        handleImageChange(
                          idx,
                          "image_url",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={img.image_type}
                      onChange={e =>
                        handleImageChange(
                          idx,
                          "image_type",
                          e.target.value
                        )
                      }
                    >
                      <option value="MAIN">MAIN</option>
                      <option value="GALLERY">GALLERY</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Display Order</label>
                    <input
                      type="number"
                      value={img.display_order}
                      onChange={e =>
                        handleImageChange(
                          idx,
                          "display_order",
                          Number(e.target.value || 1)
                        )
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>&nbsp;</label>
                    {form.images.length > 1 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeImageRow(idx)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn-add-variant"
                style={{ marginTop: 8 }}
                onClick={addImageRow}
              >
                + Add Another Image
              </button>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
